
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { hasGraphConsent, initiateGraphConsent, addMailbox } from "@/services/mailbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

const formSchema = z.object({
  email: z.string().email("Please enter a valid email address")
});

type AddMailboxDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

export function AddMailboxDialog({ isOpen, onClose, onSuccess }: AddMailboxDialogProps) {
  const { tenantId } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: ""
    }
  });

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      setIsSubmitting(true);
      setError(null);
      
      // Check if we already have consent
      const hasConsent = await hasGraphConsent();
      
      if (hasConsent) {
        // If we have consent, add the mailbox directly
        const success = await addMailbox(values.email, tenantId);
        if (success) {
          form.reset();
          onSuccess();
          onClose();
        }
      } else {
        // If we don't have consent, store the email and initiate consent flow
        sessionStorage.setItem("pendingMailboxEmail", values.email);
        initiateGraphConsent(tenantId);
      }
    } catch (error: any) {
      console.error("Error adding mailbox:", error);
      setError(error.message || "An unexpected error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Add Mailbox</DialogTitle>
          <DialogDescription>
            Connect a Microsoft 365 mailbox to set up auto-reply rules.
          </DialogDescription>
        </DialogHeader>
        
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email Address</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Enter the mailbox email address" 
                      {...field} 
                    />
                  </FormControl>
                  <FormDescription>
                    The mailbox must exist in your Microsoft 365 tenant
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Adding..." : "Add Mailbox"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
